/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { defineMessages, injectIntl } from 'react-intl'

import { EuiCode, EuiFormHelpText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AsyncRequestState, StackDeployment } from '@modules/ui-types'
import type { Column } from '@modules/cui/Table/types'
import { CuiTable } from '@modules/cui/Table'
import { CuiAlert } from '@modules/cui/Alert'
import { isAjaxNotFound } from '@modules/utils/ajax'
import prettyTime from '@modules/utils/prettyTime'

import StatPressureIndicator from '../StatPressureIndicator'
import schedule from '../../lib/schedule'
import { allSettled } from '../../lib/schedule/allSettled'

import type { WrappedComponentProps } from 'react-intl'
import type { FunctionComponent } from 'react'
import type { ClusterHealth, RecoveryInfo } from '../../reducers/clusters/clusterTypes'

type Props = {
  deployment: StackDeployment
  recoveryInfoRequest: AsyncRequestState
  recoveryInfo?: RecoveryInfo[] | null
  clusterHealth?: ClusterHealth | null
  fetchRecoveryInfo: () => Promise<any>
  fetchClusterHealth: () => Promise<any>
}

const messages = defineMessages({
  fileCount: {
    id: `shard-data-migration-table.file-count`,
    defaultMessage: `{fileCount} files`,
  },
})

const ShardDataMigrationTable: FunctionComponent<Props & WrappedComponentProps> = ({
  intl: { formatMessage },
  recoveryInfoRequest,
  recoveryInfo,
  clusterHealth,
}) => {
  if (recoveryInfoRequest.error) {
    // When no indices, Security gives us a 404
    if (isAjaxNotFound(recoveryInfoRequest.error)) {
      return null
    }

    return <CuiAlert type='warning'>{recoveryInfoRequest.error}</CuiAlert>
  }

  if (recoveryInfo == null || recoveryInfo.length === 0) {
    return null
  }

  const columns: Array<Column<RecoveryInfo>> = [
    {
      label: (
        <FormattedMessage id='shard-data-migration-table.label-index' defaultMessage='Index' />
      ),
      render: (info) => <EuiCode>{info.index}</EuiCode>,
      sortKey: `index`,
    },
    {
      label: (
        <FormattedMessage id='shard-data-migration-table.label-shard' defaultMessage='Shard' />
      ),
      render: (info) => info.shard,
      sortKey: `shard`,
    },
    {
      label: (
        <FormattedMessage id='shard-data-migration-table.label-stage' defaultMessage='Stage' />
      ),
      render: (info) => <EuiCode>{info.stage}</EuiCode>,
      sortKey: `stage`,
    },
    {
      label: (
        <FormattedMessage
          id='shard-data-migration-table.label-time-spent'
          defaultMessage='Time spent'
        />
      ),
      render: (info) => prettyTime(info.totalTime),
      sortKey: `totalTime`,
    },
    {
      label: (
        <FormattedMessage
          id='shard-data-migration-table.label-files-processed'
          defaultMessage='Files processed'
        />
      ),
      render: (info) => (
        <StatPressureIndicator
          pressure={parseFloat(String(info.files.percent).slice(0, -1))}
          total={info.files.total}
          statFormatter={(fileCount) => formatMessage(messages.fileCount, { fileCount })}
        />
      ),
      sortKey: `files.total`,
    },
    {
      label: (
        <FormattedMessage
          id='shard-data-migration-table.label-data-processed'
          defaultMessage='Data processed'
        />
      ),
      render: (info) => (
        <StatPressureIndicator
          pressure={parseFloat(String(info.size.percent).slice(0, -1))}
          total={info.size.total}
        />
      ),
      sortKey: `size.total`,
    },
  ]

  return (
    <Fragment>
      <CuiTable<RecoveryInfo>
        rows={recoveryInfo}
        getRowId={(row) => row.id}
        isEmbeddedTable={true}
        columns={columns}
      />

      {clusterHealth && (
        <EuiFormHelpText>
          <FormattedMessage
            id='shard-data-migration-table.relocating-initializing-unassigned-shards'
            defaultMessage='Relocating {relocating} {relocating, plural, one {shard} other {shards}}. Initializing {initializing} {initializing, plural, one {shard} other {shards}}. {unassigned} {unassigned, plural, one {shard is} other {shards are}} unassigned.'
            values={{
              relocating: `${clusterHealth.shards.relocating}`,
              initializing: `${clusterHealth.shards.initializing}`,
              unassigned: `${clusterHealth.shards.unassigned}`,
            }}
          />
        </EuiFormHelpText>
      )}
    </Fragment>
  )
}

export default injectIntl(
  schedule(
    ShardDataMigrationTable,
    ({ fetchRecoveryInfo, fetchClusterHealth }) =>
      allSettled([fetchRecoveryInfo(), fetchClusterHealth()]),
    [[`deployment`, `id`]],
  ),
)
